import { useState, useEffect } from "react";

import PartnershipBusinessInformation from "./Steps/PartnershipBusinessInformation";
import PartnershipBusinessOwnersInformation from "./Steps/PartnershipBusinessOwnersInformation";
import PartnershipBusinessDocuments from "./Steps/PartnershipBusinessDocuments";
import PartnershipCompleteOnboarding from "./Steps/PartnershipCompleteOnboarding";
import HeaderTitle from "../../Components/HeaderTitle";
import styles from "./Partnership.module.scss";
import useDocumentsByCustomerSubType from "../../hooks/useDocumentsByCustomerSubType";
import { BusinessTypes } from "../../../Interfaces";
import { ICorporateCustomer } from "../../hooks/useCorporateCustomers";

export enum AllPartnershipBusinessSteps {
  BUSINESS_INFORMATION = 0,
  BUSINESS_OWNERS_INFORMATION = 1,
  BUSINESS_DOCUMENTS = 2,
  COMPLETE_ONBOARDING = 3,
}

interface IPartnership {
  onComplete: () => void;
  onBack?: () => void;
  onBackHandlerReady?: (handler: (() => void) | null) => void;
  formData?: any;
  resumeCustomerData?: ICorporateCustomer;
  onStepChange?: (step: number) => void;
  navigateToStep?: number; // Allow parent to control which step to navigate to
}

const Partnership: React.FC<IPartnership> = ({
  onComplete,
  onBack,
  onBackHandlerReady,
  formData = {},
  onStepChange,
  resumeCustomerData,
  navigateToStep,
}) => {
  const [currentStep, setCurrentStep] = useState(
    AllPartnershipBusinessSteps.BUSINESS_INFORMATION,
  );

  const { data: partnershipDocuments, isLoading: isLoadingDocuments } =
    useDocumentsByCustomerSubType(BusinessTypes.PARTNERSHIP.toString());

  // Sync with parent stepper navigation (subtract 1 because parent stepper has "Get Started" as step 0)
  useEffect(() => {
    if (navigateToStep !== undefined && navigateToStep >= 1) {
      const internalStep = navigateToStep - 1;
      if (
        internalStep !== currentStep &&
        internalStep >= 0 &&
        internalStep <= AllPartnershipBusinessSteps.COMPLETE_ONBOARDING
      ) {
        setCurrentStep(internalStep);
      }
    }
  }, [navigateToStep]);

  const handleProceed = (_step: number) => {
    setCurrentStep(_step);
    // Notify parent about step change (add 1 because "Get Started" is step 0 in parent stepper)
    if (onStepChange) {
      onStepChange(_step + 1);
    }
  };

  // Notify parent when current step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep + 1);
    }
  }, [currentStep, onStepChange]);

  const handleBackStep = () => {
    // Navigate backwards through steps
    switch (currentStep) {
      case AllPartnershipBusinessSteps.COMPLETE_ONBOARDING:
        setCurrentStep(AllPartnershipBusinessSteps.BUSINESS_DOCUMENTS);
        if (onStepChange) {
          onStepChange(AllPartnershipBusinessSteps.BUSINESS_DOCUMENTS + 1);
        }
        break;
      case AllPartnershipBusinessSteps.BUSINESS_DOCUMENTS:
        setCurrentStep(AllPartnershipBusinessSteps.BUSINESS_OWNERS_INFORMATION);
        if (onStepChange) {
          onStepChange(
            AllPartnershipBusinessSteps.BUSINESS_OWNERS_INFORMATION + 1,
          );
        }
        break;
      case AllPartnershipBusinessSteps.BUSINESS_OWNERS_INFORMATION:
        setCurrentStep(AllPartnershipBusinessSteps.BUSINESS_INFORMATION);
        if (onStepChange) {
          onStepChange(AllPartnershipBusinessSteps.BUSINESS_INFORMATION + 1);
        }
        break;
      case AllPartnershipBusinessSteps.BUSINESS_INFORMATION:
        // We're at the first step, go back to parent step
        if (onBack) {
          onBack();
        }
        break;
      default:
        break;
    }
  };

  // Expose the back handler to parent via callback
  useEffect(() => {
    if (onBackHandlerReady) {
      onBackHandlerReady(handleBackStep);
    }
    // Cleanup: unregister handler when component unmounts
    return () => {
      if (onBackHandlerReady) {
        onBackHandlerReady(null);
      }
    };
  }, [currentStep, onBackHandlerReady, handleBackStep]);

  const getStepTitle = () => {
    switch (currentStep) {
      case AllPartnershipBusinessSteps.BUSINESS_INFORMATION:
        return "Business Information";
      case AllPartnershipBusinessSteps.BUSINESS_OWNERS_INFORMATION:
        return "Partner";
      case AllPartnershipBusinessSteps.BUSINESS_DOCUMENTS:
        return "Business Documents";
      case AllPartnershipBusinessSteps.COMPLETE_ONBOARDING:
        return "Complete Onboarding";
      default:
        return "";
    }
  };

  const getStepSubtitle = () => {
    if (currentStep === AllPartnershipBusinessSteps.COMPLETE_ONBOARDING) {
      return "Review your onboarding progress and complete any outstanding steps.";
    }
    return "Kindly enter your information below";
  };

  return (
    <div className={styles.partnership}>
      <HeaderTitle>{getStepTitle()}</HeaderTitle>
      <p className={styles.subtitle}>{getStepSubtitle()}</p>

      <div className={styles.content}>
        {(() => {
          switch (currentStep) {
            case AllPartnershipBusinessSteps.BUSINESS_INFORMATION:
              return (
                <PartnershipBusinessInformation
                  resumeCustomerData={resumeCustomerData}
                  handleProceed={handleProceed}
                  formData={formData}
                />
              );

            case AllPartnershipBusinessSteps.BUSINESS_OWNERS_INFORMATION:
              return (
                <PartnershipBusinessOwnersInformation
                  resumeCustomerData={resumeCustomerData!}
                  handleProceed={handleProceed}
                  formData={formData}
                />
              );

            case AllPartnershipBusinessSteps.BUSINESS_DOCUMENTS:
              return (
                <PartnershipBusinessDocuments
                  resumeCustomerData={resumeCustomerData}
                  handleProceed={handleProceed}
                  formData={formData}
                  documentTypes={partnershipDocuments?.data!}
                />
              );

            case AllPartnershipBusinessSteps.COMPLETE_ONBOARDING:
              return (
                <PartnershipCompleteOnboarding
                  resumeCustomerData={resumeCustomerData!}
                  setCurrentStep={setCurrentStep}
                  onComplete={onComplete}
                  formData={formData}
                />
              );

            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
};

export default Partnership;
